import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ArrowLeft, Check, Copy, Smartphone, SmartphoneNfc } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useState, type ReactNode } from 'react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import { AuthCodeField, AuthError, AuthHeading, authLinkClass, AuthNote, AuthSubmit, getAuthSession, mfaSchema, useAuth, useAuthNavigation } from '@/app/auth';

import { useCodeReset, useCodeStatus, useSingleSubmit } from './-code-reset';

const GOOGLE_AUTHENTICATOR = 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&gl=US';
const HOW_TO = 'https://canaltech.com.br/apps/google-authenticator-como-usar/';

export const Route = createFileRoute('/login/mfa/setup')({
  beforeLoad: () => {
    const state = getAuthSession().getState();
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- contrato do TanStack Router
    if (state.status !== 'challenge' || state.challenge !== 'mfaSetup') throw redirect({ to: '/login' });
  },
  component: MfaSetupScreen,
});

type Values = z.infer<ReturnType<typeof mfaSchema>>;

function secretFrom(uri: string | undefined) {
  return uri ? (new URL(uri).searchParams.get('secret') ?? undefined) : undefined;
}

function Step({ index, title, children }: { index: number; title: ReactNode; children?: ReactNode }) {
  return (
    <li className="group/step relative flex gap-3.5 pb-6 last:pb-0">
      <span aria-hidden className="absolute top-8 bottom-1 left-[13px] w-px bg-border group-last/step:hidden" />
      <span aria-hidden className="relative inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-wash text-xs font-semibold text-accent-text ring-1 ring-accent-line ring-inset">
        {index}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-3 pt-0.5">
        <div className="text-sm text-foreground">{title}</div>
        {children}
      </div>
    </li>
  );
}

function CopySecret({ secret }: { secret: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard
      .writeText(secret)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-card py-1.5 pr-1.5 pl-3">
      <code className="min-w-0 flex-1 font-mono text-xs break-all text-foreground select-all">{secret}</code>
      <button
        type="button"
        onClick={copy}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-accent-text transition-colors duration-(--lume-dur-fast) hover:bg-accent-wash"
      >
        {copied ? <Check aria-hidden className="size-3.5" /> : <Copy aria-hidden className="size-3.5" />}
        <span aria-live="polite">{copied ? t`Copiada` : t`Copiar chave`}</span>
      </button>
    </div>
  );
}

function MfaSetupScreen() {
  const { t } = useLingui();
  const { session, pending, error, mfaSetupUri } = useAuth();
  const follow = useAuthNavigation(session);
  const form = useForm<Values>({ resolver: zodResolver(mfaSchema()), defaultValues: { code: '' } });
  const secret = secretFrom(mfaSetupUri);
  useCodeReset(form, error, pending);
  const codeStatus = useCodeStatus();
  const typed = useWatch({ control: form.control, name: 'code' });
  const { typing } = codeStatus;
  useEffect(() => typing(typed), [typed, typing]);

  const onSubmit = useSingleSubmit(
    form.handleSubmit(async ({ code }) => {
      const state = await session.confirmMfaSetup(code);
      await codeStatus.settle(state);
      await follow(state);
    }),
  );

  return (
    <form className="flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <AuthHeading icon={SmartphoneNfc} title={<Trans>Configuração de aplicativo autenticador (MFA)</Trans>}>
        <Trans>Para acessar o sistema, você deverá configurar uma autenticação MFA. São três passos rápidos.</Trans>
      </AuthHeading>
      <AuthError message={error} />
      <ol className="flex flex-col">
        <Step
          index={1}
          title={
            <Trans>
              Instale o{' '}
              <a href={GOOGLE_AUTHENTICATOR} target="_blank" rel="noreferrer">
                Google Authenticator
              </a>{' '}
              no seu celular. Dúvidas?{' '}
              <a href={HOW_TO} target="_blank" rel="noreferrer">
                Veja como usar
              </a>
              .
            </Trans>
          }
        />
        <Step index={2} title={<Trans>No aplicativo, escolha &quot;Ler QR Code&quot; e aponte a câmera para o código.</Trans>}>
          {mfaSetupUri ? (
            <figure className="flex flex-col items-start gap-3">
              {/* O QR precisa de fundo claro em qualquer tema para a câmera ler. */}
              <div className="rounded-2xl bg-stage-foreground p-2.5 shadow-2 ring-1 ring-border">
                <QRCodeSVG value={mfaSetupUri} size={168} level="H" marginSize={1} title={t`QR Code do autenticador`} />
              </div>
              {secret ? (
                <figcaption className="flex w-full flex-col gap-1.5 text-xs text-muted-foreground">
                  <Trans>Ou digite a chave no aplicativo:</Trans>
                  <CopySecret secret={secret} />
                </figcaption>
              ) : null}
            </figure>
          ) : null}
        </Step>
        <Step index={3} title={<Trans>Digite o código de 6 dígitos que aparece no aplicativo.</Trans>}>
          <AuthCodeField
            control={form.control}
            label={t`Código de verificação`}
            error={form.formState.errors.code}
            status={codeStatus.status}
            onComplete={() => {
              if (!pending) void onSubmit();
            }}
          />
        </Step>
      </ol>
      <AuthSubmit pending={pending || codeStatus.status === 'success'}>
        <Trans>Acessar</Trans>
      </AuthSubmit>
      <AuthNote icon={Smartphone}>
        <Trans>
          Caso tenha perdido seu dispositivo, entre em contato com o administrador para definir um novo código de autenticação.
        </Trans>
      </AuthNote>
      <p className="text-center">
        <Link to="/login" className={authLinkClass}>
          <ArrowLeft aria-hidden className="size-4" />
          <Trans>Voltar para o login</Trans>
        </Link>
      </p>
    </form>
  );
}
